import React, { useEffect, useState } from 'react';
import { parseFrontmatter } from '../utils/frontmatter';

const escapeHtml = value => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const renderInline = value => {
  const safeValue = escapeHtml(value);
  return safeValue
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>');
};

const parseMarkdown = source => {
  const lines = source.split(/\r?\n/);
  const blocks = [];
  let paragraph = [];
  let list = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
    paragraph = [];
  };

  const flushList = () => {
    if (list.length === 0) return;
    blocks.push({ type: 'list', items: list });
    list = [];
  };

  lines.forEach(line => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      return;
    }

    const listItem = trimmed.match(/^[-*]\s+(.+)$/);
    if (listItem) {
      flushParagraph();
      list.push(listItem[1]);
      return;
    }

    flushList();
    paragraph.push(trimmed);
  });

  flushParagraph();
  flushList();

  return blocks;
};

const MarkdownContent = ({ src, className, headingIds }) => {
  const [content, setContent] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    fetch(src)
      .then(response => {
        if (!response.ok) throw new Error('Markdown content could not be loaded');
        return response.text();
      })
      .then(markdown => {
        if (active) {
          setContent(parseFrontmatter(markdown).content);
          setError(false);
        }
      })
      .catch(() => {
        if (active) setError(true);
      });

    return () => {
      active = false;
    };
  }, [src]);

  if (error) return <p className={className}>The article could not be loaded.</p>;
  if (!content) return <p className={className}>Loading article...</p>;

  const blocks = parseMarkdown(content);
  let headingIndex = 0;

  return (
    <div className={className}>
      {blocks.map((block, index) => {
        if (block.type === 'list') {
          return (
            <ul key={`list-${index}`}>
              {block.items.map((item, itemIndex) => (
                <li
                  key={`item-${itemIndex}-${item}`}
                  dangerouslySetInnerHTML={{ __html: renderInline(item) }}
                />
              ))}
            </ul>
          );
        }

        if (block.type === 'heading') {
          const Heading = `h${block.level}`;
          const headingId = headingIds[headingIndex];
          headingIndex += 1;
          return (
            <Heading
              key={`heading-${index}`}
              id={headingId}
              dangerouslySetInnerHTML={{ __html: renderInline(block.text) }}
            />
          );
        }

        return (
          <p
            key={`paragraph-${index}`}
            dangerouslySetInnerHTML={{ __html: renderInline(block.text) }}
          />
        );
      })}
    </div>
  );
};

MarkdownContent.defaultProps = {
  src: '',
  className: '',
  headingIds: [],
};

export default MarkdownContent;
